// src/services/topUpSalesOrderCreation.ts
// ---------------------------------------
// Stock Top-ups Phase 4b — the ISOLATED SO builder that runs at the Top-up
// Confirm & Send (TopUpApprovalView). It is DELIBERATELY separate from the
// child-based fit-out engine (salesOrderCreation.ts / soRulesEngine.ts): a stock
// top-up has no children, so it can't reuse that pipeline. It reads the editable
// Top-up Product Mapping (top_up_product_map, db-158) and writes the SAME output
// tables the standard flow does — `request_sales_orders` (flat lines) + a
// `request_sales_order_pushes` row — so the existing Sales Orders admin consumes
// it unchanged.
//
// SO rules (John, 12 Aug 2026 — docs/stock-top-ups-plan.md §F):
//   • Stationery Packs  → ONE SO per approved pack UNIT (3 × Y3/4 = three SOs),
//     each using that year's product code. All packs share the base suffix "STA"
//     with a per-unit counter (STA, STA1..STA9, STAA..) so order numbers stay unique.
//   • Every OTHER (bulk) section → ONE SO with a SINGLE summary line using that
//     section's single product code (BULKBAGS / BULKSTATIONERY / BULKHATS /
//     BULKSOCKS / BULK-LB-&-WB). The line comment carries that product's DESCRIPTION
//     (from unleashed_products, e.g. "Bulk school bags - All states"); the actual $ is
//     a summary Amount the team enters later. The picker view shows the itemized
//     breakdown from the stock_top_up_lines directly (not from this comment). Other-
//     Stock splits into the lunch/water summary SO and the $150-reimbursement SO.
//   • The $150 reimbursement → ONE line, qty 1, the amount as the unit price.
//   • EVERY SO also carries exactly ONE $0 DISCOUNT line (matches the fit-out SOs).
//   • A declined section produces NO SO (declined lines never reach here).
//   • order_number = `<ref>-<suffix>`; ALL top-up SOs are parked on `hold` so they
//     surface to the Pickers side (a dedicated top-up picker view is the next
//     piece) and are NOT auto-pushed to Unleashed.
//
// NOTE (12 Aug 2026): these behaviours are hard-coded here for now; the Top-up SO
// Rules module (Manage → Sales Order Sync → Top-up Requests) will move them into an
// editable table that this builder reads, with these values as the seed.

import { getById as getDeliveryAddressById } from "../data/deliveryAddresses";
import { createQueuedPush } from "../data/salesOrderPushes";
import { createSalesOrder } from "../data/salesOrders";
import { getShelterSalesMeta } from "../data/shelters";
import { listTopUpProductMap } from "../data/topUpProductMap";
import { listTopUpSoRules } from "../data/topUpSoRules";
import { getProductDescriptionMap } from "../data/unleashedProducts";
import { createPickerTask } from "../utils/actions";
import { logAudit } from "../utils/auditLog";
import { logger } from "../utils/logger";
import { lineLabel, SECTION_LABELS } from "./stockTopUpForm";

// An APPROVED capture line handed to the builder (a subset of stock_top_up_lines
// plus the reviewer's approved quantity). Only approved lines are passed in.
export interface TopUpApprovedLine {
  section: string;
  item_key: string;
  item_name?: string | null;
  colour?: string | null;
  size?: string | null;
  year_level?: string | null;
  level?: string | null;
  approved_qty?: number | null;
  amount?: number | null; // Other-Stock reimbursement only
}

// A Top-up Product Mapping row (top_up_product_map) — kept loose so this service
// doesn't depend on the repo's row type. `suffix` moved to the SO rule (db-160).
export interface TopUpProductMapLike {
  section: string;
  item_key: string;
  product_code: string;
}

// A Top-up SO Rule row (top_up_so_rules, db-159 + db-160) — the editable per-section
// routing behaviour, including the SO order-number suffix. Kept loose so the pure
// builder doesn't depend on the repo's row type.
export interface TopUpSoRuleLike {
  section: string;
  variant: string; // '' | 'reimbursement'
  line_mode: string; // 'per_unit' | 'summary' | 'amount'
  include_discount: boolean;
  suffix: string; // 'STA' (packs, base for the counter) / 'BSTA' / 'SBAG' / …
  deactivated?: boolean;
}

// The seed / fallback rules — identical to the db-159 + db-160 seed. Used when the
// rules table is empty (e.g. before the migration is applied) so the builder still works.
export const DEFAULT_TOP_UP_RULES: TopUpSoRuleLike[] = [
  { section: "packs", variant: "", line_mode: "per_unit", include_discount: true, suffix: "STA" },
  { section: "bulk", variant: "", line_mode: "summary", include_discount: true, suffix: "BSTA" },
  { section: "bags", variant: "", line_mode: "summary", include_discount: true, suffix: "SBAG" },
  { section: "other", variant: "", line_mode: "summary", include_discount: true, suffix: "LBWB" },
  { section: "hats", variant: "", line_mode: "summary", include_discount: true, suffix: "HATS" },
  { section: "socks", variant: "", line_mode: "summary", include_discount: true, suffix: "SOCK" },
  {
    section: "other",
    variant: "reimbursement",
    line_mode: "amount",
    include_discount: true,
    suffix: "RMB",
  },
];

export interface BuiltTopUpSOLine {
  line_number: number;
  product_code: string;
  order_quantity: number;
  unit_price: number;
  line_comments: string | null;
}

export interface BuiltTopUpSO {
  order_number: string;
  suffix: string; // base suffix stored on the push row (packs share "STA")
  suffix_sequence: number | null; // the pack-unit index (null for the single-SO sections)
  section: string;
  product_code: string;
  lines: BuiltTopUpSOLine[];
}

// The per-pack-unit counter token appended to the shared "STA" base: unit 0 → ""
// (so the first pack SO is `<ref>-STA`), 1..9 → the digit, 10+ → A, B, C … .
export function packCounterToken(index: number): string {
  if (index <= 0) return "";
  if (index <= 9) return String(index);
  return String.fromCharCode(65 + (index - 10)); // A=10, B=11, …
}

// Every SO carries exactly one $0 DISCOUNT line (matches the fit-out SOs). It is
// always the LAST line of the SO.
const DISCOUNT_CODE = "DISCOUNT";
function discountLine(lineNumber: number): BuiltTopUpSOLine {
  return {
    line_number: lineNumber,
    product_code: DISCOUNT_CODE,
    order_quantity: 1,
    unit_price: 0,
    line_comments: null,
  };
}

// A whole-number approved quantity (0 when blank / negative / non-numeric).
function approvedQtyOf(l: TopUpApprovedLine): number {
  return Math.max(0, Math.trunc(Number(l.approved_qty ?? 0)));
}

// Is this Other-Stock line the $150 reimbursement variant (vs lunch/water qty)?
function isReimbursementLine(l: TopUpApprovedLine): boolean {
  return l.section === "other" && (l.item_key === "reimbursement" || l.amount != null);
}

// The rule variant a line belongs to (Other-Stock splits into the reimbursement rule
// and the default one; every other section uses the default variant "").
function variantOf(l: TopUpApprovedLine): string {
  return isReimbursementLine(l) ? "reimbursement" : "";
}

// A stable section order for deterministic SO output (packs first, then the rest).
const SECTION_RANK: Record<string, number> = {
  packs: 0,
  bulk: 1,
  bags: 2,
  other: 3,
  hats: 4,
  socks: 5,
};

// Build the Top-up Sales Orders (PURE — no I/O) from the approved lines + the product
// mapping + the SO rules (top_up_so_rules). Each line's rule (section + variant) drives
// how it becomes SOs: `per_unit` = one SO per approved unit; `summary` = one SO with a
// single summary line for the section (breakdown in the comment; $ set later); `amount`
// = one SO, one line carrying its amount. `include_discount` adds the $0 DISCOUNT line.
// Returns [] when nothing maps / has a rule.
export function buildTopUpSalesOrders(
  approvedLines: TopUpApprovedLine[],
  mapRows: TopUpProductMapLike[],
  rules: TopUpSoRuleLike[],
  refNo: string,
  descByCode?: Map<string, string>
): BuiltTopUpSO[] {
  const map = new Map<string, TopUpProductMapLike>();
  for (const r of mapRows) map.set(`${r.section}|${r.item_key}`, r);
  const ruleMap = new Map<string, TopUpSoRuleLike>();
  for (const r of rules) {
    if (!r.deactivated) ruleMap.set(`${r.section}|${r.variant}`, r);
  }

  const resolveMap = (l: TopUpApprovedLine): TopUpProductMapLike | undefined => {
    if (l.section === "packs") return map.get(`packs|${l.item_key}`);
    if (l.section === "other") {
      return map.get(`other|${isReimbursementLine(l) ? "reimbursement" : ""}`);
    }
    return map.get(`${l.section}|`);
  };
  const resolveRule = (l: TopUpApprovedLine): TopUpSoRuleLike | undefined =>
    ruleMap.get(`${l.section}|${variantOf(l)}`);

  // Append the $0 DISCOUNT line when the rule asks for it (always the last line).
  const withDiscount = (lines: BuiltTopUpSOLine[], rule: TopUpSoRuleLike): BuiltTopUpSOLine[] =>
    rule.include_discount ? [...lines, discountLine(lines.length + 1)] : lines;

  const orders: BuiltTopUpSO[] = [];

  // ── per_unit: ONE SO per approved unit, counter shared across a suffix ──
  // (Stationery Packs share the "STA" base → STA, STA1, STA2 …). The suffix comes
  // from the SO RULE (db-160), not the product mapping.
  const suffixCounter = new Map<string, number>();
  for (const l of approvedLines) {
    const rule = resolveRule(l);
    if (!rule || rule.line_mode !== "per_unit") continue;
    const m = resolveMap(l);
    if (!m) continue;
    const base = rule.suffix;
    const qty = approvedQtyOf(l);
    for (let u = 0; u < qty; u++) {
      const idx = suffixCounter.get(base) ?? 0;
      const suffix = `${base}${packCounterToken(idx)}`;
      orders.push({
        order_number: `${refNo}-${suffix}`,
        suffix: base,
        suffix_sequence: idx,
        section: l.section,
        product_code: m.product_code,
        lines: withDiscount(
          [
            {
              line_number: 1,
              product_code: m.product_code,
              order_quantity: 1,
              unit_price: 0,
              line_comments: lineLabel(l),
            },
          ],
          rule
        ),
      });
      suffixCounter.set(base, idx + 1);
    }
  }

  // ── summary / amount: ONE SO per mapped row (grouped by its suffix) ──
  //   summary → a single summary line for the whole section (breakdown in the comment,
  //             $ entered later); amount → one line carrying the line's amount ($150).
  interface Group {
    m: TopUpProductMapLike;
    section: string;
    rule: TopUpSoRuleLike;
    items: TopUpApprovedLine[];
  }
  const groups = new Map<string, Group>();
  for (const l of approvedLines) {
    const rule = resolveRule(l);
    if (!rule || (rule.line_mode !== "summary" && rule.line_mode !== "amount")) continue;
    const m = resolveMap(l);
    if (!m) continue;
    const qty = rule.line_mode === "amount" ? 1 : approvedQtyOf(l);
    if (qty <= 0) continue;
    let g = groups.get(rule.suffix);
    if (!g) {
      g = { m, section: l.section, rule, items: [] };
      groups.set(rule.suffix, g);
    }
    g.items.push(l);
  }
  const grouped = [...groups.values()].sort(
    (a, b) => (SECTION_RANK[a.section] ?? 99) - (SECTION_RANK[b.section] ?? 99)
  );
  for (const g of grouped) {
    const summaryLine: BuiltTopUpSOLine =
      g.rule.line_mode === "amount"
        ? {
            line_number: 1,
            product_code: g.m.product_code,
            order_quantity: 1,
            unit_price: Number(g.items[0].amount ?? 0),
            line_comments: g.items[0].item_name ?? lineLabel(g.items[0]),
          }
        : {
            line_number: 1,
            product_code: g.m.product_code,
            order_quantity: 1,
            unit_price: 0,
            // Bulk summary SOs carry the product's DESCRIPTION in the line comment
            // (John, 13 Aug — e.g. BULKBAGS → "Bulk school bags - All states"). The
            // picker view still reads the itemized breakdown from the stock_top_up_lines
            // directly, not from here, and the completion append tolerates a non-empty
            // base comment. Falls back to null when the code has no description.
            line_comments: descByCode?.get(g.m.product_code) ?? null,
          };
    orders.push({
      order_number: `${refNo}-${g.rule.suffix}`,
      suffix: g.rule.suffix,
      suffix_sequence: null,
      section: g.section,
      product_code: g.m.product_code,
      lines: withDiscount([summaryLine], g.rule),
    });
  }

  return orders;
}

// Request-level fields the Top-up SO header + picker task need.
export interface TopUpSalesOrderMeta {
  refNo?: string | null;
  shelterCode?: string | null;
  shelterName?: string | null;
  state?: string | null;
  deliveryAddress?: string | null; // resolved one-line text (fallback)
  deliveryAddressId?: string | null; // FK to delivery_addresses → structured SO block
  deliveryAddressOverride?: string | null; // one-off address text (no structured row)
  contactName?: string | null;
  salesPersonEmail?: string | null; // the staffer who confirmed
}

export interface TopUpQueueResult {
  created: number;
  orderNumbers: string[];
}

// Build + queue the Top-up Sales Orders at Confirm & Send. Best-effort — the caller
// (TopUpApprovalView.handleConfirm) has ALREADY committed the approval, so a failure
// here must never surface as a confirm failure. Each SO is written flat +
// parked on `hold` (db-113) for the Pickers side.
export async function queueTopUpSalesOrders(
  requestId: number,
  approvedLines: TopUpApprovedLine[],
  meta: TopUpSalesOrderMeta
): Promise<TopUpQueueResult> {
  const [mapRows, rules, shelterMeta, deliveryRow, descByCode] = await Promise.all([
    listTopUpProductMap(),
    listTopUpSoRules(),
    getShelterSalesMeta(meta.shelterCode ?? undefined),
    getDeliveryAddressById(meta.deliveryAddressId),
    getProductDescriptionMap(),
  ]);
  // Fall back to the seed rules if the table is empty (e.g. db-159 not yet applied),
  // so the builder still produces the expected SOs.
  const effectiveRules = rules.length > 0 ? rules : DEFAULT_TOP_UP_RULES;

  const orders = buildTopUpSalesOrders(
    approvedLines,
    mapRows,
    effectiveRules,
    meta.refNo ?? "",
    descByCode
  );
  if (orders.length === 0) return { created: 0, orderNumbers: [] };

  // Structured delivery block — the SAME shape the normal (fit-out) SO builder writes
  // (salesOrderCreation.ts): prefer the request's registered delivery_addresses row so
  // Suburb/City, State/Region + Post Code land in their own fields; fall back to the
  // one-off override / resolved text (street only) when there's no structured row.
  const deliveryHeader = deliveryRow
    ? {
        delivery_contact: meta.contactName ?? null,
        delivery_name: deliveryRow.address_name ?? meta.shelterName ?? null,
        delivery_street_address: deliveryRow.street ?? null,
        delivery_street_address_2: deliveryRow.street2 ?? null,
        delivery_suburb: deliveryRow.suburb ?? null,
        delivery_city: deliveryRow.suburb_city ?? deliveryRow.suburb ?? null,
        delivery_region: deliveryRow.state ?? null,
        delivery_post_code: deliveryRow.postcode ?? null,
        delivery_country: "Australia",
        delivery_instruction: deliveryRow.delivery_instruction ?? null,
      }
    : {
        delivery_contact: meta.contactName ?? null,
        delivery_name: meta.shelterName ?? null,
        delivery_street_address: meta.deliveryAddressOverride ?? meta.deliveryAddress ?? null,
        delivery_country: "Australia",
      };

  const today = new Date().toISOString().slice(0, 10);
  const written: string[] = [];
  for (const o of orders) {
    const header = {
      order_number: o.order_number,
      customer_code: meta.shelterCode ?? "",
      customer_reference: `Top-up ${SECTION_LABELS[o.section] ?? o.section}`.slice(0, 500),
      warehouse_code: shelterMeta.default_warehouse,
      comments: null,
      order_date: today,
      required_date: today,
      quote_expiry_date: today,
      sales_order_group: shelterMeta.sales_group,
      sales_person_email: meta.salesPersonEmail ?? null,
      ...deliveryHeader,
    };
    const { error } = await createSalesOrder(header, o.lines, requestId);
    if (error) {
      logger.warn("queueTopUpSalesOrders: createSalesOrder failed:", error.message);
      continue;
    }
    // Parked on `hold` (db-113): top-up SOs go to the Pickers side, not the API.
    await createQueuedPush(o.order_number, requestId, {
      suffix: o.suffix,
      suffix_sequence: o.suffix_sequence ?? undefined,
      payee_identifier: null,
      status: "hold",
    });
    // One `top_up` picker task per SO — surfaces this SO in the Pickers list as its own
    // card (scoped by so_number). The description is the section label.
    createPickerTask("top_up", {
      requestId,
      refNo: meta.refNo ?? "",
      shelterName: meta.shelterName ?? "",
      shelterCode: meta.shelterCode ?? "",
      state: meta.state ?? "",
      description: `Top-up ${SECTION_LABELS[o.section] ?? o.section}`,
      soNumber: o.order_number,
    });
    written.push(o.order_number);
  }

  if (written.length > 0) {
    logAudit(
      "top_up_sales_orders_queued",
      { request_id: requestId, count: written.length, order_numbers: written },
      requestId
    );
  }
  return { created: written.length, orderNumbers: written };
}
